/**
 * Imports
 *
 * Import any modules or components here.
 */
import { i18n, outputDiv } from "commonAssets/js/constants";
import { createElement } from "commonAssets/lib/createElement.js";

/**
 * Navigation Class
 * 
 * Navigation class representing the website navigation.
 * 
 * @class
 * @param {Array} pages - An array of objects representing website pages.
 * @param {string} slug - The current page's slug.
 
 */
export class Navigation {
    constructor(pages, slug, getPage, dates = "") {
        this.pages = pages;
        this.slug = slug;
        this.dates = dates;
        this.getPage = getPage;
        this.element = this.createNav();
    }

    /**
     * createNav()
     *
     * Creates the website navigation HTML element.
     *
     * @memberof Navigation
     * @instance
     * @returns {HTMLElement} - The website navigation HTML element.
     */
    createNav() {
        const navBarToggler = createElement(
            "button",
            {
                class: "navbar-toggler",
                type: "button",
                "data-bs-toggle": "collapse",
                "data-bs-target": "#app-navbar",
                "aria-controls": "app-navbar",
                "aria-expanded": "false",
                "aria-label": "Toggle navigation",
            },
            [],
            "MENU"
        );

        const navItems = this.pages.map((page) => {
            const regex = /--[\w-]+/g;
            const matches = page.PageTitle.match(regex);
            const strippedMatches = matches
                ? matches.map((match) => match.substring(2))
                : null;
            if (strippedMatches == "hidden") return "";

            // set isActive to empty string and aria_current to false
            let isActive = "",
                aria_current = false;
            // If the slug matches the page name, add the "active" class
            if (
                this.slug == page.PageName ||
                (this.slug === "" && page.PageName === "home")
            ) {
                isActive = " active";
                aria_current = true;
            }

            // Create an object to conditionally add the "aria-current" attribute
            const ariaCurrentAttr = aria_current
                ? { "aria-current": "page" }
                : {};

            // Create the nav item
            return createElement(
                "li",
                {
                    class: "nav-item",
                },
                [
                    createElement(
                        "a",
                        {
                            href: page.PageName,
                            class: "nav-link px-3" + isActive,
                            ...ariaCurrentAttr,
                        },
                        [],
                        page.PageTitle
                    ),
                ]
            );
        });

        const navigation = createElement(
            "div",
            {
                class: "collapse navbar-collapse",
                id: "app-navbar",
                role: "navigation",
                "aria-label": "menu",
            },
            [
                createElement(
                    "ul",
                    {
                        class: "navbar-nav nav-pills me-auto mb-2 mb-lg-0",
                    },
                    navItems
                ),
            ]
        );

        const submitClaimBtn = createElement(
            "a",
            {
                href: "submit-claim",
                id: "subClaimBtn",
                class: "btn btn-lg text-uppercase",
                style: this.pages.some(
                    (page) => page.PageName === "submit-claim"
                )
                    ? ""
                    : "display: none;",
            },
            [],
            i18n.getTranslation("nav_submit_claim_button")
        );

        const navBar = createElement(
            "nav",
            {
                class: "navbar navbar-expand-lg navbar-light bg-light border-top border-bottom",
            },
            [
                createElement(
                    "div",
                    {
                        class: "container",
                    },
                    [navBarToggler, submitClaimBtn, navigation]
                ),
            ]
        );

        return navBar;
    }

    /**
     * Handles changes to the URL path by listening for popstate events and clicks on links,
     * and loads the corresponding page content.
     * @param {Object[]} pages - An array of page objects, each containing a slug and HTML content.
     * @param {string} slug - The initial page slug to load.
     * @param {string[]} dates - An array of date strings to be used for date filtering.
     * @returns {void}
     */
    pathChange(pages, slug, dates) {
        window.addEventListener("popstate", (e) => {
            // clear out the id="output" <section>
            outputDiv.innerHTML = ``;
            // the slug
            const slug = window.location.pathname.split("/")[1];
            // adds css class selector to active link
            isPageActive(slug);
            // returns the page html
            this.getPage(pages, slug, dates);
        });

        // Listen for clicks on links
        document.addEventListener("click", (e) => {
            const link = e.target.closest("a");
            if (link && link.href.startsWith(window.location.origin)) {
                const url = new URL(link.href);
                const languageFromQuery = url.searchParams.get("language");
                const languageFromPath = url.pathname
                    .split("/")
                    .filter(Boolean)[0];
                const isLanguageQuery =
                    languageFromQuery &&
                    i18n.languages.includes(languageFromQuery);
                const isLanguagePath =
                    languageFromPath &&
                    i18n.languages.includes(languageFromPath);

                if (isLanguageQuery || isLanguagePath) {
                    return;
                }

                // prevent default happenings
                e.preventDefault();
                // if the link has a target="_blank" attribute, open it in a new tab
                if (
                    link.getAttribute("target") &&
                    link.getAttribute("target") == "_blank"
                ) {
                    window.open(link.href);
                    return;
                }
                // clear out the id="output" <section>
                outputDiv.innerHTML = ``;
                // html history api pushstate
                window.history.pushState({}, "", link.href);
                // the slug
                const slug = window.location.pathname.split("/")[1];
                // adds css class selector to active link
                isPageActive(slug);
                // returns the page html
                this.getPage(pages, slug, dates);
            }
            // closes the navbar after a clickity-click uwu
            document.querySelector("#app-navbar").classList.remove("show");
        });
    }
}

/**
 * Active class selector
 *
 * Adds or removes the "active" class from Navigation links based on the
 * current URL hash. Also sets the "aria-current" attribute to "page" on the
 * active link.
 *
 * @param {string} params - The URL hash to match against the Navigation links.
 * @returns {void}
 */
function isPageActive(params) {
    const navLinks = document.querySelectorAll(".nav-link");
    navLinks.forEach((link) => {
        const href = link.getAttribute("href");
        if (params === href) {
            link.classList.add("active");
            link.setAttribute("aria-current", "page");
        } else {
            link.classList.remove("active");
            link.removeAttribute("aria-current");
        }
    });
}
